/**
 * useProjects - Supabase-realtime project list for the current user.
 *
 * Priority:
 *   1. Supabase `projects` table (canonical, realtime)
 *   2. localStorage scan (offline cache / pre-Supabase projects)
 *
 * localStorage is read only for a SIGNED-OUT visitor. It is never merged into an
 * authenticated account: those keys predate account scoping, so on a shared browser
 * they can hold another person's project. This ensures a smooth
 * transition while existing projects are migrated. It is never authoritative.
 *
 * Realtime: any INSERT/UPDATE/DELETE on the projects table for this user is
 * reflected instantly across all open tabs without a page refresh.
 */

import { useCallback, useEffect, useState } from "react";
import { supabase, ensureSupabaseConfig } from "@/integrations/supabase/client";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { getSavedProjectIds } from "@/services/projectLoader";
import { useAuth } from "@/auth/AuthContext";
import { deriveProjectIdentity } from "@/services/projectNaming";
import type { NuvraSchema } from "@/core/schema/nuvraSchema";

export interface ProjectSummary {
  id:          string;
  uuid?:       string;
  name:        string;
  appType:     string;
  prompt?:     string;
  updatedAt:   string | null;
  previewUrl:  string | null;
  thumbnailUrl: string | null;
  /** The project's real schema, when stored. Powers a faithful card preview
   *  (the actual app structure) until a runtime screenshot exists. */
  schema:      NuvraSchema | null;
  source:      "supabase" | "local";
}

/** Parse the stored schema, which may be a JSON string or an object (or absent).
 *  Best-effort: a malformed value yields null and the card falls back gracefully. */
function parseSchema(raw: unknown): NuvraSchema | null {
  if (!raw) return null;
  try {
    const obj = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (obj && typeof obj === "object" && Array.isArray((obj as NuvraSchema).pages)) {
      return obj as NuvraSchema;
    }
  } catch {
    /* malformed - fall through to null */
  }
  return null;
}

/* =========================================================
   localStorage fallback - reads nuvra-project-* keys
   ========================================================= */

function loadFromLocalStorage(): ProjectSummary[] {
  return getSavedProjectIds().map((id) => {
    try {
      const raw  = localStorage.getItem(`nuvra-project-${id}`);
      const data = raw ? JSON.parse(raw) : null;
      return {
        id,
        name:       data?.settings?.name ?? `Project ${id}`,
        appType:    data?.settings?.type  ?? "App",
        updatedAt:  data?.lastSaved ? new Date(data.lastSaved).toISOString() : null,
        previewUrl: null,
        thumbnailUrl: null,
        schema:     parseSchema(data?.schema ?? data?.nuvra_schema),
        source:     "local" as const,
      };
    } catch {
      return { id, name: `Project ${id}`, appType: "App", updatedAt: null, previewUrl: null, thumbnailUrl: null, schema: null, source: "local" as const };
    }
  });
}

/* =========================================================
   Supabase row → ProjectSummary
   ========================================================= */

/** A name is "generic" when it is empty or an auto placeholder. Such projects
 *  should never show "Untitled Project" to the user when we can derive a real
 *  title from the prompt they were built from. */
function isGenericProjectName(name: string): boolean {
  const n = name.trim();
  return !n || /^untitled\b/i.test(n) || /^project\s/i.test(n);
}

/** The display name: the real name if it is meaningful, otherwise a concise
 *  product identity derived from the prompt through the shared naming
 *  intelligence (never a raw prompt slice), otherwise a stable fallback. */
function displayProjectName(row: Record<string, unknown>): string {
  const name   = (row.name as string) ?? "";
  if (!isGenericProjectName(name)) return name;
  const prompt = (row.prompt as string) ?? "";
  if (prompt) {
    const identity = deriveProjectIdentity(prompt);
    if (identity.source !== "fallback") return identity.name;
  }
  return name || `Project ${row.project_key ?? row.id}`;
}

/** Most-recent-first by updatedAt (which already falls back to created_at).
 *  Client-side so it can never fail on a missing/absent timestamp column. */
function sortByRecency(list: ProjectSummary[]): ProjectSummary[] {
  return [...list].sort((a, b) => {
    const ta = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
    const tb = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
    return tb - ta;
  });
}

function rowToSummary(row: Record<string, unknown>): ProjectSummary {
  return {
    id:         (row.project_key as string) || (row.id as string),
    uuid:       row.id as string,
    name:       displayProjectName(row),
    appType:    (row.app_type as string) || "App",
    prompt:     (row.prompt as string) || undefined,
    // Prefer updated_at, but fall back to created_at: some live DBs are missing
    // the updated_at column (migration drift), and the list must still render.
    updatedAt:  ((row.updated_at ?? row.created_at) as string) ?? null,
    previewUrl: (row.preview_url as string) ?? null,
    thumbnailUrl: (row.thumbnail_url as string) ?? null,
    schema:     parseSchema(row.nuvra_schema),
    source:     "supabase",
  };
}

/* =========================================================
   Hook
   ========================================================= */

/** Transient supabase-js failures that a retry resolves: the multi-tab
 *  navigator.locks contention on the auth token ("lock:sb-...-auth-token was
 *  released because another request stole it"), aborts, and network drops.
 *  These must never surface to the customer as a terminal failure. */
function isTransientQueryError(message: string): boolean {
  return /lock:sb-.*auth-token|LockAcquireTimeout|stole it|AbortError|Failed to fetch|NetworkError|ERR_NETWORK/i.test(message);
}

const RETRY_DELAYS_MS = [400, 1200];

export function useProjects(): { projects: ProjectSummary[]; loading: boolean; queryError: string | null; retry: () => void } {
  const { user, loading: authLoading } = useAuth();
  const [projects,    setProjects]    = useState<ProjectSummary[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [queryError,  setQueryError]  = useState<string | null>(null);
  const [configReady, setConfigReady] = useState(false);
  const [retryKey,    setRetryKey]    = useState(0);
  const retry = useCallback(() => setRetryKey(k => k + 1), []);

  useEffect(() => {
    ensureSupabaseConfig().then(() => setConfigReady(true));
  }, []);

  // Key the subscription off the stable user id rather than the user object, so a new user
  // object with the same id does not tear down and rebuild the realtime channel.
  const uid = user?.id;
  useEffect(() => {
    if (authLoading || !configReady) { setLoading(true); return; }

    let channel: RealtimeChannel | null = null;
    let cancelled = false;

    if (!uid) {
      setProjects(loadFromLocalStorage());
      setLoading(false);
      return;
    }
    // uid is narrowed to a string past the guard; capture it so the nested async
    // closures below keep that narrowed type (TS re-widens a guarded outer binding
    // when it is read inside a nested function scope).
    const activeUid: string = uid;

    async function init() {
      setLoading(true);
      setQueryError(null);

      let data:  Record<string, unknown>[] | null = null;
      let error: { message: string } | null = null;
      for (let attempt = 0; ; attempt++) {
        try {
          const res = await supabase.from("projects").select("*").eq("user_id", activeUid);
          data = res.data; error = res.error;
        } catch (e) {
          error = { message: e instanceof Error ? e.message : String(e) };
        }
        if (cancelled || !error || attempt >= RETRY_DELAYS_MS.length || !isTransientQueryError(error.message)) break;
        await new Promise(r => setTimeout(r, RETRY_DELAYS_MS[attempt]));
      }

      if (!cancelled) {
        if (error) {
          console.error("[useProjects] projects query failed:", error.message, error);
          setQueryError(error.message);
        } else if (data && data.length > 0) {
          setProjects(sortByRecency(data.map(rowToSummary)));
        } else {
          setProjects([]);
        }
        setLoading(false);
      }

      if (cancelled) return;

      // Every mounted useProjects instance gets its own topic. Creative Studio
      // can coexist with the global sidebar/project switcher, and Supabase's
      // client reuses a channel object when the topic is identical. Reusing the
      // old `projects:user:${uid}` topic across multiple hook instances could
      // therefore make the second instance call `.on('postgres_changes', ...)`
      // on an already-joining/subscribed channel, which newer supabase-js
      // versions correctly reject and which previously crashed this route.
      const channelName = `projects:user:${uid}:${crypto.randomUUID()}`;

      try {
        channel = supabase
          .channel(channelName)
          .on(
            "postgres_changes",
            { event: "*", schema: "public", table: "projects", filter: `user_id=eq.${uid}` },
            () => {
              if (cancelled) return;
              supabase
                .from("projects")
                .select("*")
                .eq("user_id", activeUid)
                .then(({ data: fresh, error: refErr }) => {
                  if (!cancelled && !refErr) setProjects(sortByRecency((fresh ?? []).map(rowToSummary)));
                });
            },
          )
          .subscribe((status, subscribeError) => {
            if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
              console.warn("[useProjects] realtime unavailable; project list will remain query-backed.", subscribeError);
            }
          });
      } catch (realtimeError) {
        // Realtime is an enhancement, not a route dependency. Never let a
        // subscription race/configuration failure crash Creative Studio.
        console.warn("[useProjects] realtime subscription unavailable:", realtimeError);
        channel = null;
      }
    }

    void init();

    return () => {
      cancelled = true;
      if (channel) {
        // removeChannel fully tears down and unregisters the topic from the
        // client. This is safer here than only calling channel.unsubscribe(),
        // especially when the effect is restarted by auth/config/retry changes.
        void supabase.removeChannel(channel);
        channel = null;
      }
    };
  }, [uid, authLoading, configReady, retryKey]);

  return { projects, loading, queryError, retry };
}
